import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  applyConsent,
  getConsent,
  hasDecided,
  saveConsent,
} from "@/services/consent/consent";
import { CookieConsentBanner } from "./CookieConsentBanner";

interface ConsentContextValue {
  /** Re-open the consent banner so the user can change their choice. */
  openPreferences: () => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

/**
 * Renders the cookie-consent banner (on first visit, or when re-opened from
 * Settings) and exposes `openPreferences()` to descendants. Analytics init is
 * applied via `applyConsent()` on every save.
 */
export function ConsentProvider({
  children,
}: {
  children: ReactNode;
}): React.ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(() => !hasDecided());
  const [initialAnalytics, setInitialAnalytics] = useState<boolean>(
    () => getConsent()?.analytics ?? false,
  );

  const openPreferences = useCallback(() => {
    setInitialAnalytics(getConsent()?.analytics ?? false);
    setIsOpen(true);
  }, []);

  const handleSave = useCallback((choice: { analytics: boolean }) => {
    const state = saveConsent(choice);
    void applyConsent(state);
    setIsOpen(false);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({ openPreferences }),
    [openPreferences],
  );

  return (
    <ConsentContext.Provider value={value}>
      {isOpen && (
        <CookieConsentBanner
          initialAnalytics={initialAnalytics}
          onSave={handleSave}
        />
      )}
      {children}
    </ConsentContext.Provider>
  );
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return ctx;
}
